import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import * as moment from 'moment';
import { DATE_PATTERN, ModalDialogType } from 'src/app/@core/constants';
import { PosConfirmationService } from 'src/app/@core/services/pos-confirmation.service';
import { PosNoEnterCreditRequestReasonModalService } from 'src/app/@core/services/pos-no-enter-credit-request-reason-modal.service';
import { ExceptionSolutionService } from 'src/app/services/exception-solution.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UserAndSellerShowMode } from './../../../../@core/constants';
import { PosCustomerTableModalService } from './../../../../@core/services/pos-customer-table-modal.service';
import { PosUserAndSellerListModalService } from './../../../../@core/services/pos-user-and-seller-list-modal.service';
import { Customer } from './../../../../models/customer';
import { CreditStatusFranchisedResponseModel } from './../../../../models/response/customerCreditStatusResponseModel';
import { CustomerTableModel } from './../../../../models/table-models/customer-table';
import { User } from './../../../../models/user';
import { AuthService } from './../../../../services/auth.service';
import { ConfigurationService } from './../../../../services/configuration.service';
import { CreditService } from './../../../../services/credit.service';
import { CustomerService } from './../../../../services/customer.service';
@Component({
  selector: 'app-customer-information',
  templateUrl: './customer-information.component.html',
  styleUrls: ['./customer-information.component.sass'],
})
export class CustomerInformationComponent implements OnInit, OnDestroy {
  currentCustomer: Customer;
  searchForm: FormGroup;
  currentCustomerCreditConditions: CreditStatusFranchisedResponseModel;
  selectedSeller: CustomerTableModel | User | undefined;
  @Output() currentCustomerChange: EventEmitter<{
    customer: Customer;
    customerCreditConditions: CreditStatusFranchisedResponseModel;
    selectedSeller: CustomerTableModel | User | undefined;
  }>;
  @Output() isCurrentCustomerChanging: EventEmitter<boolean>;
  @Input() hideButtons: boolean = false;
  @Input() hideCreditInformation: boolean = false;
  @Input() hideUserModal: boolean = false;
  constructor(
    private customerService: CustomerService,
    private customerTableModalService: PosCustomerTableModalService,
    private formBuilder: FormBuilder,
    private creditService: CreditService,
    private route: ActivatedRoute,
    private configurationService: ConfigurationService,
    private authenticationService: AuthService,
    private localStorageService: LocalStorageService,
    private userAndSellerListModalService: PosUserAndSellerListModalService,
    private confirmDialogService: PosConfirmationService,
    private noEnterCreditRequestModalService: PosNoEnterCreditRequestReasonModalService,
    private readonly exceptionSolutionService: ExceptionSolutionService
  ) {
    this.searchForm = this.formBuilder.group({
      searchCriteria: this.formBuilder.control(''),
    });

    this.currentCustomer = new Customer();
    this.currentCustomerCreditConditions =
      new CreditStatusFranchisedResponseModel();
    this.currentCustomerChange = new EventEmitter();
    this.isCurrentCustomerChanging = new EventEmitter();
  }

  async ngOnInit(): Promise<void> {
    this.route.params.subscribe(async (params: Params) => {
      let code = '';

      if (params.id) {
        code = params.id;
      } else {
        code = this.localStorageService.get('selectedCustomer') ?? '';
      }

      if (code) {
        this.searchForm.patchValue({ searchCriteria: code });
        this.searchForm.markAsDirty();
        await this.handleSubmit();
      }
    });
  }

  ngOnDestroy() {
    const code = this.searchForm.get('searchCriteria');
    this.localStorageService.set('selectedCustomer', code?.value);
  }

  async handleSubmit(): Promise<void> {
    if (await this.validatePreApproveInProgress()) {
      this.creditService.setPreApprove(undefined);
      this.resetCurrentCustomerInfo();
      this.isCurrentCustomerChanging.emit(true);

      if (!this.hideUserModal) {
        this.selectedSeller = await this.showAndGetUserSeller();

        if (this.isNotUserSelected(this.selectedSeller)) {
          this.showWarningMessageForNotSelectedUserSeller();
          return;
        }
      }
      else {
        this.selectedSeller = this.authenticationService.readProfileInStorage();
      }


      if (this.searchForm.valid && this.searchForm.dirty) {
        await this.fetchCustomerInfo();

        await this.fetchCustomerCreditInfo(this.currentCustomer);

        this.emitSelectedCustomerChange();
      }
    }

  }

  private async validatePreApproveInProgress() {
    let response = true;

    const preApprove = this.creditService.getPreApprove();

    if (preApprove !== undefined) {
      if (!preApprove.isCreditRequestCreated) {
        const confirmDialogUserResponse = await this.confirmDialogService.showAsync(
          `¡Atención!`,
          `¿Esta seguro de salir, sin ingresar una solicitud de credito?`,
          ModalDialogType.WARNING
        );
        if (confirmDialogUserResponse) {
          response = await this.noEnterCreditRequestModalService.show(this.currentCustomer, '');
        } else {
          response = false;
        }
      }
    }

    return response;
  }

  async handleSearchCustomerClick() {
    const customerTableModel = await this.customerTableModalService
      .show()
      .content?.customerSelectedEvent.toPromise();

    this.searchForm
      .get('searchCriteria')
      ?.patchValue(customerTableModel?.code || '');
    this.searchForm.markAsDirty();
    await this.handleSubmit();
  }

  private emitSelectedCustomerChange(): void {
    this.currentCustomerChange.emit({
      customer: this.currentCustomer,
      customerCreditConditions: this.currentCustomerCreditConditions,
      selectedSeller: this.selectedSeller,
    });
  }

  private resetCurrentCustomerInfo(): void {
    this.currentCustomerCreditConditions =
      new CreditStatusFranchisedResponseModel();
    this.currentCustomer = new Customer();
  }

  private async fetchCustomerCreditInfo(customer: Customer): Promise<void> {
    if (!this.hideCreditInformation) {
      const { branch } =
        this.configurationService.readSelectedBranchAndSalesOrg();
      const { username } = this.authenticationService.readProfileInStorage();

      this.currentCustomerCreditConditions = await this.creditService
        .getCustomerCreditStatus(customer, branch.code, username)
        .toPromise();
    }
  }

  private async fetchCustomerInfo(): Promise<void> {
    this.currentCustomer = await this.customerService
      .readCustomerByCode(this.searchForm.value.searchCriteria, {
        branchCode:
          this.configurationService.readSelectedBranchAndSalesOrg().branch.code,
        sellerCode:
          (this.isCustomer(this.selectedSeller)
            ? this.selectedSeller.code
            : this.selectedSeller?.username) || '',
        menu: 'CUSTOMERS',
      })
      .toPromise();
  }

  getCalculateAge(birthdate: String): number | undefined {
    let age = 0;
    if (birthdate != undefined)
      age = moment().diff(moment(birthdate.toString(), DATE_PATTERN), 'years');

    return isNaN(age) ? undefined : age;
  }
  private async showAndGetUserSeller(): Promise<
    User | CustomerTableModel | undefined
  > {
    return this.userAndSellerListModalService.show(
      UserAndSellerShowMode.USERS,
      true
    );
  }
  private isNotUserSelected(
    selectedUserSeller: User | CustomerTableModel | undefined
  ): boolean {
    return selectedUserSeller == undefined;
  }

  private showWarningMessageForNotSelectedUserSeller() {
    this.exceptionSolutionService.showAlertWithSolution(`Vendedor`, 'Debe seleccionar un vendedor para poder continuar con el proceso');
  }

  isCustomer(
    value: CustomerTableModel | User | undefined
  ): value is CustomerTableModel {
    return (value as CustomerTableModel).fullName !== undefined;
  }
}
